// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {AttrsSchema, Component, jsx} from 'panel';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

import {emitTrackEvent} from 'common/util/dom';
import {filterAttributes} from 'common/util';
import {pluralize} from 'common/util/string';
import {currentProjectStore} from 'common/report/stores/project-details';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {getEntityTypePermissions, isSharingEnabledForProject} from 'common/report/util/saved-entities';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {promptSharing} from 'common/widgets/sharing';
import {customEventsStore} from 'common/report/stores/custom-events';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {useStore} from 'common/util/component-helpers';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {ResourceType} from 'common/report/constants';
import {IronContextRegistry} from 'common/report/context-registry';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {confirmDelete} from 'common/components/confirm';
import {THEME} from 'common/components/confirm/types';
import {EntrySectionUpdates, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {CustomEventPropertyFilter} from 'common/widgets/property-filter-menu/models/segfilter';
import {SEGMENT_SEPARATOR} from 'common/report/funnels/models/constants';

import {DisplayContext as PropertyFilterDisplayContext} from '../property-filter-menu/models/types';
import {PropertyFilter} from '../property-filter-menu/models/property-filter';
import {template} from './template';
import css from './index.inline.styl';
import {EventSelector, MPCustomEvent} from './models/events';

const MAX_NAME_LENGTH = 255;
const DISPATCHED_EVENTS = {
  MODAL_CLOSED: `modalClosed`,
  DELETED: `deletedCustomEvent`,
  DELETING: `deletingCustomEvent`,
  SAVING: `savingCustomEvent`,
  SAVED: `savedCustomEvent`,
};

interface CustomEventModalState {
  error: Obj;
  open: boolean;
  stagedCustomEvent: MPCustomEvent;
}

export interface CustomEventModalAttrs {
  open: boolean;
  'project-has-people': boolean;
  'project-id': number;
  'selected-custom-event-id'?: number;
  'workspace-id': number;
  'all-data-groups': boolean;
  'project-as-of-date'?: string;
  'track-name'?: string;
}

export interface CustomEventModalParams {
  dataGroupIds?: Set<string | null>;
}

export class CustomEventModalInternal extends Component<
  CustomEventModalState,
  CustomEventModalAttrs,
  unknown,
  unknown,
  IronContextRegistry,
  CustomEventModalParams
> {
  declare nameInputEl: any;
  declare initialCustomEvent: MPCustomEvent;

  static get attrsSchema(): AttrsSchema<CustomEventModalAttrs> {
    return {
      ...super.attrsSchema,
      open: `boolean`,
      'project-has-people': `boolean`,
      'project-id': `string`,
      'selected-custom-event-id': `number`,
      'workspace-id': `number`,
      'all-data-groups': `boolean`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'track-name': `string`,
    };
  }

  static get dispatchedEvents() {
    return Object.values(DISPATCHED_EVENTS);
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    switch (attr) {
      case `selected-custom-event-id`:
        if (this.attr(attr)) {
          this.updateStagedCustomEvent();
        } else {
          this.clearStagedCustomEvent();
        }
        break;
      case `open`: {
        const open = this.attr(attr);
        this.update({open});
        break;
      }
      default:
        break;
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.initialCustomEvent = new MPCustomEvent({...this.state.stagedCustomEvent});

    useStore(this, currentProjectStore).fetch();
    useStore(this, customEventsStore).fetch({projectId: getProjectId(), workspaceId: getWorkspaceId()});
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      contexts: [`i18n` as const],
      defaultState: {
        error: {},
        open: false,
        stagedCustomEvent: new MPCustomEvent(),
        affectedMessageIds: [],
      },
      defaultParams: {dataGroupIds: new Set<string | null>()},
      params: {dataGroupIds: Set<string | null>},
      helpers: {
        MAX_NAME_LENGTH,
        PropertyFilterDisplayContext,
        pick,
        pluralize,
        filterAttributes,
        getSharingBannerAttrs: () => ({
          'project-id': this.attr(`project-id`),
          'entity-type': EntityType.CustomEvent,
        }),
        showShareManagement: () => {
          if (isSharingEnabledForProject(currentProjectStore.value)) {
            const entityTypePermissions = getEntityTypePermissions(this.attr(`project-id`), EntityType.CustomEvent);
            return entityTypePermissions.can_share;
          }
          return false;
        },
        handleModalChange: ({detail}) => {
          if (detail && detail.state === `closed`) {
            this.update({open: false});
            requestAnimationFrame(() => {
              this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.MODAL_CLOSED));
              // clear errors and other transient state when modal closes
              this.update(pick(this.config.defaultState, [`error`]));
            });
          }
        },

        hasUnsavedChanges: () => {
          return !isEqual(this.initialCustomEvent, this.state.stagedCustomEvent);
        },

        updateQueryEvents: (updates: EntrySectionUpdates<QueryEventEntry>) => {
          const {stagedCustomEvent} = this.state;
          stagedCustomEvent.eventSelectors = updates.entries.map(
            ({filters, event, filtersOperator}) =>
              new EventSelector({
                propertyFilters: filters as CustomEventPropertyFilter[],
                event: event.custom ? {name: event.label, id: event.id} : event.value,
                filtersOperator,
              }),
          );
          this.update({stagedCustomEvent});
        },

        captureTracking: (ev) => {
          const {eventName, props} = ev.detail;
          ev.stopPropagation();
          this.track(eventName, props || {});
        },
        isNewCustomEvent: () => !this.state.stagedCustomEvent.id,
        isStagedCustomEventValid: () => {
          const {stagedCustomEvent, error} = this.state;
          return stagedCustomEvent.isValid() && !Object.values(error).find(Boolean);
        },
        clearNameErrors: () => this.update({error: {}}),
        clickSave: ({andShare = false} = {}) => {
          if (this.helpers.isStagedCustomEventValid()) {
            this.saveChanges({andShare});
          }
        },
        nameInputInserted: ({elm}) => {
          this.nameInputEl = elm;
        },
        updateCustomEventName: (ev) => {
          const stagedCustomEvent = this.state.stagedCustomEvent.updateName(ev.target.value.trim());
          this.update({stagedCustomEvent});
          this.validateStagedCustomEvent();
        },
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  validateStagedCustomEvent() {
    const {name, id} = this.state.stagedCustomEvent;
    const error = {
      invalidName: !name,
      duplicateName: false,
      invalidSeparator: false, // Our segments use / as a separator, so we don't allow it in custom event names
    };

    if (name) {
      const otherCustomEvents = this.customEvents.filter((customEvent) => customEvent.id !== id);
      error.duplicateName = !!otherCustomEvents.find(
        (customEvent) => customEvent.name.toLowerCase() === name.toLowerCase(),
      );
      error.invalidSeparator = name.includes(SEGMENT_SEPARATOR);
    }
    this.update({error});
  }

  getEntries(): QueryEventEntry[] {
    const {stagedCustomEvent} = this.state;

    return stagedCustomEvent.eventSelectors.map((eventSelector) => ({
      event: {
        custom: eventSelector.eventIsCustom(),
        label: eventSelector.getEventLabel(),
        value: eventSelector.getEventName(),
        ...(eventSelector.eventIsCustom()
          ? {
              id: eventSelector.getEventId(),
            }
          : {}),
      },
      filters: eventSelector.propertyFilters.map(
        (propertyFilter) => new PropertyFilter(propertyFilter.getAttributes()),
      ),
      filtersOperator: eventSelector.filtersOperator,
      type: ResourceType.Event,
    }));
  }

  isDataGroupsEnabled() {
    const inlineFilters = this.getEntries().flatMap((entry) => entry.filters);
    const userProp = inlineFilters.find((filter) => filter.resourceType === ResourceType.User && !filter.dataGroupId);
    if (userProp) {
      return false;
    }
    return this.attr(`all-data-groups`);
  }

  async handleDeleteClick() {
    const didConfirm = await confirmDelete({
      title: this.t(`custom-event.header.delete`),
      description: this.t(`custom-event.delete-description`),
      theme: THEME.DELETE,
      'confirm-button-label': this.t(`common.delete`),
    });

    if (didConfirm) {
      const {id} = this.state.stagedCustomEvent;
      this.track(`[Custom Event] Delete`);
      this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.DELETING));
      try {
        await customEventsStore.delete(id, {
          customEventId: id,
          projectId: getProjectId(),
          workspaceId: getWorkspaceId(),
        });

        this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.DELETED, {detail: {id}}));
      } catch (err) {
        addToast({
          isStatic: true,
          message: (
            <div sel='.text'>
              Your custom event could not be deleted. If this issue persists please{` `}
              <a attrs={{href: `https://mixpanel.com/get-support`, target: `_blank`}}>contact support</a>.
            </div>
          ),
          type: ToastType.ERROR,
        });
        console.error(err);
      }
    }
  }

  async saveChanges({andShare = false}) {
    const {stagedCustomEvent} = this.state;
    const trackingEvent = stagedCustomEvent.id ? `[Custom Event] Update` : `[Custom Event] Save New`;
    this.track(trackingEvent);
    this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.SAVING));
    try {
      const result = await customEventsStore.post({
        customEventId: stagedCustomEvent.id,
        customEvent: stagedCustomEvent.toSerializedCustomEvent(),
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      });

      // force event definitions to reload
      eventDefinitionsStore.refresh({projectId: getProjectId(), workspaceId: getWorkspaceId()});

      this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.SAVED, {detail: {customEvent: result}}));
      this.clearStagedCustomEvent();
      if (andShare) {
        await promptSharing({
          type: SharableEntityType.CustomEvent,
          id: result.id,
          'project-id': this.attr(`project-id`),
        });
      }
    } catch (err) {
      addToast({
        isStatic: true,
        message: <div sel='.text'>{this.t(`custom-event.errors.error-saving`)}</div>,
        type: ToastType.ERROR,
      });
      console.warn(err);
    }
  }

  updateStagedCustomEvent() {
    const customEventId = this.attr(`selected-custom-event-id`);
    if (customEventId) {
      this.update({stagedCustomEvent: MPCustomEvent.fromSerializedCustomEvent(customEventsStore.value[customEventId])});
    }
  }

  clearStagedCustomEvent() {
    this.update({stagedCustomEvent: new MPCustomEvent()});
  }

  get customEvents() {
    return Object.values(customEventsStore.value);
  }

  track(eventName, props = {}) {
    emitTrackEvent(this, {
      eventName,
      props: {
        ...props,
        ...this.state.stagedCustomEvent.toTrackingData(),
        [`usage context`]: TrackingUsageContext.CustomEventsModal,
      },
    });
  }

  /** Provisionally enables `All Events` for approved projects, this is disabled by default for performance reasons */
  canUseAllEvents() {
    return getProjectId() === `2911513`;
  }
}

export const CustomEventModalInternalEl = defineAndMakeJsxFactory(`mp-custom-event-modal`, CustomEventModalInternal);
export const CustomEventModalInternalReact = panel2react(`mp-custom-event-modal`, CustomEventModalInternal);
